/**
 * Quiz API Client
 * Handles all API calls to the quiz backend
 */

import type {
  QuizType,
  GetQuestionsResponse,
  SubmitQuizRequest,
  QuizResult
} from '../types';

const API_BASE_URL = '/api/quiz';

class QuizAPI {
  /**
   * Get all active quiz types
   */
  async getTypes(): Promise<QuizType[]> {
    const response = await fetch(`${API_BASE_URL}/types`);

    if (!response.ok) {
      throw new Error('Failed to fetch quiz types');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get questions for a specific quiz type
   */
  async getQuestions(quizTypeId: number): Promise<GetQuestionsResponse> {
    const response = await fetch(`${API_BASE_URL}/${quizTypeId}/questions`);

    if (!response.ok) {
      throw new Error('Failed to fetch quiz questions');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Submit quiz answers and get matches
   */
  async submitQuiz(request: SubmitQuizRequest): Promise<QuizResult> {
    const response = await fetch(`${API_BASE_URL}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to submit quiz' }));
      throw new Error(error.message || 'Failed to submit quiz');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get cached quiz results by session ID
   */
  async getResults(sessionId: string): Promise<QuizResult | null> {
    const response = await fetch(`${API_BASE_URL}/results/${sessionId}`);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error('Failed to fetch quiz results');
    }

    const data = await response.json();
    return data.data;
  }
}

export const quizAPI = new QuizAPI();
